export type FooterColumnType = 'brand' | 'contact' | 'quickAccess' | 'campaigns' | 'legal';

export type FooterColumnSide = 'left' | 'right';

export interface FooterColumnSlot {
  type: FooterColumnType;
  id?: number;
  side?: FooterColumnSide;
}

export type FooterIconLayout = 'row' | 'grid';
export type FooterAlign = 'left' | 'center' | 'right';

export function resolveFooterAlign(value: unknown): FooterAlign {
  if (value === 'center' || value === 'right') return value;
  return 'left';
}

export function resolveFooterColumnSide(value: unknown): FooterColumnSide {
  return value === 'right' ? 'right' : 'left';
}

/** Sütun grubunun konumu (içerik hizası değil). */
export function getFooterJustify(align: FooterAlign): 'flex-start' | 'center' | 'flex-end' {
  if (align === 'center') return 'center';
  if (align === 'right') return 'flex-end';
  return 'flex-start';
}

/** Sütun içi yazı/ikonlar her zaman solda kalır. */
export const FOOTER_COLUMN_CONTENT_ALIGN = {
  textAlign: 'left',
  alignItems: 'flex-start',
} as const;

export function getFooterColumnKey(slot: FooterColumnSlot): string {
  return slot.type === 'quickAccess' ? `quickAccess:${slot.id ?? 0}` : slot.type;
}

export function buildDefaultFooterColumnOrder(
  quickAccessColumns: Array<{ id: number }>
): FooterColumnSlot[] {
  return [
    { type: 'brand' },
    { type: 'contact' },
    ...quickAccessColumns.map((column) => ({ type: 'quickAccess' as const, id: column.id })),
  ];
}

export function resolveFooterColumnOrder(
  saved: FooterColumnSlot[] | null | undefined,
  quickAccessColumns: Array<{ id: number }>,
  options?: { includeCampaigns?: boolean; includeLegal?: boolean }
): FooterColumnSlot[] {
  const defaults = buildDefaultFooterColumnOrder(quickAccessColumns);
  const qaIds = new Set(quickAccessColumns.map((column) => column.id));
  const includeCampaigns = options?.includeCampaigns === true;
  const includeLegal = options?.includeLegal === true;

  if (!Array.isArray(saved) || saved.length === 0) {
    const next = [...defaults];
    if (includeCampaigns) next.push({ type: 'campaigns' });
    if (includeLegal) next.push({ type: 'legal', side: 'right' });
    return next;
  }

  const seen = new Set<string>();
  const resolved: FooterColumnSlot[] = [];

  for (const slot of saved) {
    if (!slot?.type) continue;
    if (slot.type === 'quickAccess' && (slot.id == null || !qaIds.has(slot.id))) continue;
    if (slot.type === 'campaigns' && !includeCampaigns) continue;
    if (slot.type === 'legal' && !includeLegal) continue;
    const key = getFooterColumnKey(slot);
    if (seen.has(key)) continue;
    seen.add(key);
    resolved.push({
      type: slot.type,
      ...(slot.type === 'quickAccess' ? { id: slot.id } : {}),
      side: resolveFooterColumnSide(slot.side),
    });
  }

  for (const slot of defaults) {
    const key = getFooterColumnKey(slot);
    if (seen.has(key)) continue;
    seen.add(key);
    resolved.push(slot);
  }

  if (includeCampaigns && !seen.has('campaigns')) {
    resolved.push({ type: 'campaigns' });
  }

  if (includeLegal && !seen.has('legal')) {
    resolved.push({ type: 'legal', side: 'right' });
  }

  return resolved;
}

export function moveFooterColumn(
  order: FooterColumnSlot[],
  index: number,
  direction: -1 | 1
): FooterColumnSlot[] {
  const target = index + direction;
  if (target < 0 || target >= order.length) return order;
  const next = [...order];
  const current = next[index];
  next[index] = next[target];
  next[target] = current;
  return next;
}

export function addQuickAccessSlot(
  order: FooterColumnSlot[],
  columnId: number
): FooterColumnSlot[] {
  const slot: FooterColumnSlot = { type: 'quickAccess', id: columnId };
  if (order.some((item) => getFooterColumnKey(item) === getFooterColumnKey(slot))) {
    return order;
  }
  return [...order, slot];
}

export function removeQuickAccessSlot(
  order: FooterColumnSlot[],
  columnId: number
): FooterColumnSlot[] {
  return order.filter((slot) => !(slot.type === 'quickAccess' && slot.id === columnId));
}

export function splitFooterColumnsBySide(order: FooterColumnSlot[]): {
  left: FooterColumnSlot[];
  right: FooterColumnSlot[];
} {
  const left: FooterColumnSlot[] = [];
  const right: FooterColumnSlot[] = [];
  for (const slot of order) {
    if (resolveFooterColumnSide(slot.side) === 'right') {
      right.push(slot);
    } else {
      left.push(slot);
    }
  }
  return { left, right };
}

export function setFooterColumnSide(
  order: FooterColumnSlot[],
  key: string,
  side: FooterColumnSide
): FooterColumnSlot[] {
  const next = order.map((slot) =>
    getFooterColumnKey(slot) === key ? { ...slot, side } : slot
  );
  const { left, right } = splitFooterColumnsBySide(next);
  return [...left, ...right];
}

export function moveFooterColumnInSide(
  order: FooterColumnSlot[],
  key: string,
  direction: -1 | 1
): FooterColumnSlot[] {
  const { left, right } = splitFooterColumnsBySide(order);
  const inRight = right.some((slot) => getFooterColumnKey(slot) === key);
  const group = inRight ? right : left;
  const index = group.findIndex((slot) => getFooterColumnKey(slot) === key);
  if (index < 0) return order;
  const moved = moveFooterColumn(group, index, direction);
  return inRight ? [...left, ...moved] : [...moved, ...right];
}
